import React, { useState } from 'react';
import '../styles/EarlyRepayment.scss';
import '../styles/loanCommon.scss';
import { Box, Card, CardContent, Grid, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Chip, FormControl, InputLabel, Select, MenuItem, Tooltip } from '@mui/material';
import { Download, Add, CheckCircle } from '@mui/icons-material';
import { Restore as RestoreIcon, Edit as EditIcon } from "@mui/icons-material";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { loanInputData, earlyRepaymentData } from '../data/mockData';
import LoanInputParameters from './LoanInputParameters';
import { trackActivity } from '../../../../utils/mixpanel';

const EarlyRepayment = ({ loanType, onLoanTypeChange }) => {
    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: 'Early Repayment', page_type: page_type })
    }

    const [inputData, setInputData] = useState({
        ...loanInputData,
    });
    const [scheduleData] = useState(earlyRepaymentData);
    const [error, setError] = useState({});
    const [openDialog, setOpenDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [prepaymentData, setPrepaymentData] = useState({
        month: '',
        amount: ''
    });

    const onRestErrorKey = (key) => {
        setError((prev) => ({ ...prev, [key]: '' }));
    };

    const onFieldChange = (key, value) => {
        setInputData({ ...inputData, [key]: value });
        setError((prev) => ({ ...prev, [key]: '' }));
    };

    const handleCalculate = () => {
        alert('Calculation logic will be implemented');
    };

    const handleAddPrepayment = () => {
        setOpenDialog(true);
    };

    const handleSavePrepayment = () => {
        alert('Prepayment added successfully');
        setOpenDialog(false);
        setPrepaymentData({ month: '', amount: '' });
    };

    const handleSaveEarlyPayment = () => {
        alert('Added successfully');
        setOpenEditDialog(false);
    };

    const handleExport = (format) => {
        alert(`Exporting schedule as ${format}`);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0
        }).format(amount);
    };

    return (
        <Box className="early-repayment-container">
            {/* Summary Metrics */}
            <Card className="action-card">
                <CardContent>
                    <Box className="card-header">
                        <Typography className='loan-heading'>Early Repayment Summary</Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            size='small'
                            startIcon={<Add />}
                            onClick={() => { handleAddPrepayment(); handleItemClicked('Add Prepayment Button') }}
                        >
                            Add Prepayment
                        </Button>
                    </Box>

                    <Grid container spacing={3} className="summary-metrics">
                        <Grid size={{ md: 3, xs: 12 }}>
                            <Box className="metric-box">
                                <Typography className="metric-label">Total Prepayments</Typography>
                                <Typography variant="h4" className="metric-value">$500,000</Typography>
                            </Box>
                        </Grid>
                        <Grid size={{ md: 3, xs: 12 }}>
                            <Box className="metric-box">
                                <Typography className="metric-label">Interest Saved</Typography>
                                <Typography variant="h4" className="metric-value">$142,500</Typography>
                            </Box>
                        </Grid>
                        <Grid size={{ md: 3, xs: 12 }}>
                            <Box className="metric-box">
                                <Typography className="metric-label">Months Reduced</Typography>
                                <Typography variant="h4" className="metric-value">8</Typography>
                            </Box>
                        </Grid>
                        <Grid size={{ md: 3, xs: 12 }}>
                            <Box className="metric-box">
                                <Typography className="metric-label">New Balance</Typography>
                                <Typography variant="h4" className="metric-value">$8,927,000</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Input Form */}
            <Card className="input-form-card">
                <CardContent>
                    <Box className="table-header">
                        <Typography className='loan-heading'>Loan Input Parameters</Typography>
                        <Box className="export-buttons">
                            <Tooltip arrow title="Revision History"><RestoreIcon className="revision-history" sx={{ cursor: 'pointer' }} onClick={() => handleItemClicked('RevisionHistoryIcon')} fontSize='small' color='primary' /></Tooltip>
                            <Tooltip arrow title="Edit"><EditIcon sx={{ cursor: 'pointer' }} onClick={() => { setOpenEditDialog({modal:true}); handleItemClicked('EditIcon') }} fontSize='small' color='primary' /></Tooltip>
                        </Box>
                    </Box>
                    <Grid container spacing={3}>
                        <LoanInputParameters inputData={inputData} handleInputChange={onFieldChange} loanType={loanType} onLoanTypeChange={onLoanTypeChange} type={'view'}/>
                    </Grid>
                </CardContent>
            </Card>

            {/* Schedule Table */}
            <Card className="table-card">
                <CardContent>
                    <Box className="table-header">
                        <Typography className='loan-heading'>Early Repayment Schedule</Typography>
                        <Box className="export-buttons">
                            <Button startIcon={<ArrowDownwardIcon />} variant="outlined" size='small' onClick={() => handleItemClicked('Export Button')}> Export </Button>
                        </Box>
                    </Box>
                    <TableContainer component={Paper} className="early-repayment-table lease-detail-table">
                        <Table className="mui-table-format" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell  sx={{minWidth: '130px'}}>Month</TableCell>
                                    <TableCell  sx={{minWidth: '130px'}}>Date</TableCell>
                                    <TableCell align="left"  sx={{minWidth: '130px'}}>Rent</TableCell>
                                    <TableCell align="left"  sx={{minWidth: '130px'}}>Debt Service</TableCell>
                                    <TableCell align="left"  sx={{minWidth: '130px'}}>Interest</TableCell>
                                    <TableCell align="left"  sx={{minWidth: '130px'}}>Principal</TableCell>
                                    <TableCell align="left"  sx={{minWidth: '130px'}}>Prepayment</TableCell>
                                    <TableCell align="left"  sx={{minWidth: '130px'}}>Outstanding</TableCell>
                                    <TableCell  sx={{minWidth: '130px'}}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scheduleData.map((row) => (
                                    <TableRow
                                        key={row.month}
                                        hover
                                        className={row.prepayment > 0 ? 'prepayment-row' : (row.recalculated ? 'recalculated-row' : '')}
                                    >
                                        <TableCell>{row.month}</TableCell>
                                        <TableCell>{row.date}</TableCell>
                                        <TableCell align="left">{formatCurrency(row.rent)}</TableCell>
                                        <TableCell align="left">{formatCurrency(row.debtService)}</TableCell>
                                        <TableCell align="left">{formatCurrency(row.interest)}</TableCell>
                                        <TableCell align="left">{formatCurrency(row.principalRepayment)}</TableCell>
                                        <TableCell align="left">
                                            {row.prepayment > 0 ? (
                                                <Chip
                                                    label={formatCurrency(row.prepayment)}
                                                    color="success"
                                                    size="small"
                                                />
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell align="left" className="outstanding-balance">
                                            {formatCurrency(row.principalOutstanding)}
                                        </TableCell>
                                        <TableCell>
                                            {row.recalculated && (
                                                <Chip
                                                    icon={<CheckCircle />}
                                                    label="Recalculated"
                                                    color="info"
                                                    size="small"
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Add Prepayment Dialog */}
            <Dialog aria-labelledby="customized-dialog-title" className='add-lease-dialog' open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle className='dialog-header'>Add Prepayment</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid size={{ md: 6, xs: 12 }}>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Month"
                                fullWidth
                                size='small'
                                variant="outlined"
                                value={prepaymentData.month}
                                onChange={(e) => setPrepaymentData({ ...prepaymentData, month: e.target.value })}
                                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                            />
                        </Grid>
                        <Grid size={{ md: 6, xs: 12 }}>
                            <TextField
                                margin="dense"
                                label="Prepayment Amount ($)"
                                fullWidth
                                size='small'
                                variant="outlined"
                                value={prepaymentData.amount}
                                onChange={(e) => setPrepaymentData({ ...prepaymentData, amount: e.target.value })}
                                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenDialog(false); handleItemClicked('Cancel Button') }} size='small'>Cancel</Button>
                    <Button onClick={() => { handleSavePrepayment(); handleItemClicked('Save Button') }} variant="contained" color="primary" size='small'>Save</Button>
                </DialogActions>
            </Dialog>

            {openEditDialog?.modal ? <Dialog aria-labelledby="customized-dialog-title" className='add-lease-dialog' maxWidth='md' open={true}>
                <DialogTitle className='dialog-header'>Edit Early Repayment</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <LoanInputParameters type={'edit'} inputData={inputData} loanType={loanType} onLoanTypeChange={onLoanTypeChange} onRestErrorKey={onRestErrorKey} error={error}/>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenEditDialog({ modal: false }); handleItemClicked('Cancel Button') }} size='small'>Cancel</Button>
                    <Button onClick={() => { handleSaveEarlyPayment(); handleItemClicked('Save Button') }} variant="contained" color="primary" size='small'>Save</Button>
                </DialogActions>
            </Dialog> : null}
        </Box>
    );
};

export default EarlyRepayment;
